/**
 * Apresentação do SGQ — matriz GUT, criticidade, prazo de CAPA e situações.
 *
 * A DECISÃO vem inteira de `@qualicloud/domain`: `gutScore`,
 * `criticalityFromScore`, `resolveCapaDueState` e `CRITICALITY_LABEL_PT_BR`.
 * (O rótulo das categorias Ishikawa vem de `ISHIKAWA_CATEGORY_LABEL_PT_BR`, em
 * `@qualicloud/contracts`, e é consumido direto pelo componente do diagrama.)
 * Este módulo não reimplementa limiar nenhum —
 * se reimplementasse, a faixa exibida na tela poderia divergir da que o CORE
 * gravou com os limiares da organização (`gut_low_max` / `gut_medium_max`), e o
 * usuário veria "Baixa" numa NC que o servidor classificou como "Alta". Aqui só
 * há tradução para pixel e para texto.
 *
 * ACESSIBILIDADE (WCAG 1.4.1 — "uso de cor"):
 * o legado pintava a criticidade só com a cor de fundo da célula
 * (`nc_list.twig:549-562`: #ccffdd / #ffeecc / #ffcccc). Isso some em
 * daltonismo, em monocromia e em impressão preto e branco — que é justamente o
 * cenário de auditoria. Cada faixa carrega aqui TRÊS sinais independentes:
 *   1. cor (identidade cromática do legado preservada nos tokens de semáforo);
 *   2. ícone de FORMA distinta (octógono / triângulo / círculo);
 *   3. TEXTO ("Alta", "Média", "Baixa") mais o SCORE numérico.
 */

import {
  AttendanceStatus,
  CapaActionStatus,
  CircularStatus,
  NonconformityStatus,
} from '@qualicloud/contracts';
import {
  CapaDueState,
  CriticalityLevel,
  CRITICALITY_LABEL_PT_BR,
  criticalityFromScore,
  DEFAULT_CRITICALITY_THRESHOLDS,
  gutScore,
  MeetingStatus,
  resolveCapaDueState,
  type CriticalityThresholds,
  type GutScore,
} from '@qualicloud/domain';
import type { BadgeTone } from '@/design-system';

// ─────────────────────────────────────────────────────────────────────────────
// Matriz GUT — as três escalas de 1 a 5
// ─────────────────────────────────────────────────────────────────────────────

export type GutFactor = 'severity' | 'urgency' | 'trend';

export interface GutLevelOption {
  readonly value: 1 | 2 | 3 | 4 | 5;
  /** Rótulo curto do nível. */
  readonly label: string;
  /** O que o nível significa. Aparece na própria lista, não só num tooltip. */
  readonly description: string;
}

export interface GutFactorDefinition {
  readonly factor: GutFactor;
  readonly label: string;
  /** Uma frase dizendo o que o fator mede. */
  readonly question: string;
  readonly levels: readonly GutLevelOption[];
}

/**
 * As escalas SÃO as do legado, texto a texto (sgq.md §A.2.1,
 * `qualidade.twig:183-220`). Preservar a redação importa: quem já usava o
 * sistema pontua pelo mesmo critério, e o histórico migrado continua comparável
 * com o que for lançado a partir de agora.
 *
 * O que muda é o NOME do campo. No legado cada fator tinha um punhado de
 * aliases aceitos pelo controller (`gravidade|Gravidade`,
 * `urgencia|urgente|Urgencia|Urgente`, `tendencia|Tendencia`) enquanto o model
 * lia outro conjunto — daí o BUG-NC-10, em que a NC era gravada com título e
 * descrição VAZIOS e a validação passava. Aqui há UM nome por fator, em inglês,
 * vindo de `gutScoreSchema`.
 */
export const GUT_FACTORS: readonly GutFactorDefinition[] = [
  {
    factor: 'severity',
    label: 'Gravidade',
    question: 'Qual é o impacto se nada for feito?',
    levels: [
      { value: 5, label: 'Extremamente grave', description: 'Impacto máximo sobre produto, paciente ou conformidade regulatória.' },
      { value: 4, label: 'Muito grave', description: 'Impacto relevante, com consequência perceptível fora do setor.' },
      { value: 3, label: 'Grave', description: 'Impacto significativo, contido dentro do processo afetado.' },
      { value: 2, label: 'Pouco grave', description: 'Impacto pequeno, absorvido pela rotina.' },
      { value: 1, label: 'Sem gravidade', description: 'Sem impacto identificado sobre o processo.' },
    ],
  },
  {
    factor: 'urgency',
    label: 'Urgência',
    question: 'Quanta pressão de tempo existe para agir?',
    levels: [
      { value: 5, label: 'Extremamente urgente', description: 'Exige ação imediata.' },
      { value: 4, label: 'Muito urgente', description: 'Precisa ser tratado nos próximos dias.' },
      { value: 3, label: 'Urgente', description: 'Precisa ser tratado nas próximas semanas.' },
      { value: 2, label: 'Pouco urgente', description: 'Pode aguardar o próximo ciclo de planejamento.' },
      { value: 1, label: 'Sem urgência', description: 'Não há prazo pressionando.' },
    ],
  },
  {
    factor: 'trend',
    label: 'Tendência',
    question: 'O que acontece se o problema não for tratado?',
    levels: [
      { value: 5, label: 'Piora imediatamente', description: 'Se não for resolvido, agrava-se de imediato.' },
      { value: 4, label: 'Piora a curto prazo', description: 'Vai piorar nas próximas semanas.' },
      { value: 3, label: 'Piora a médio prazo', description: 'Vai piorar nos próximos meses.' },
      { value: 2, label: 'Piora a longo prazo', description: 'Vai piorar, mas só no longo prazo.' },
      { value: 1, label: 'Sem tendência de piorar', description: 'Permanece como está.' },
    ],
  },
];

export function gutFactorDefinition(factor: GutFactor): GutFactorDefinition {
  const definition = GUT_FACTORS.find((item) => item.factor === factor);
  // O tipo já garante o membro; o `??` existe só para não devolver `undefined`
  // caso alguém acrescente um fator ao union sem acrescentar a definição.
  return definition ?? (GUT_FACTORS[0] as GutFactorDefinition);
}

export function gutLevelOption(factor: GutFactor, value: number): GutLevelOption | null {
  return gutFactorDefinition(factor).levels.find((level) => level.value === value) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Criticidade
// ─────────────────────────────────────────────────────────────────────────────

export interface CriticalityPresentation {
  readonly level: CriticalityLevel;
  readonly score: number;
  /** "Alta", "Média" ou "Baixa" — de `CRITICALITY_LABEL_PT_BR`. */
  readonly label: string;
  /** Frase completa para leitor de tela. */
  readonly srText: string;
  readonly className: string;
  readonly iconName: 'high' | 'medium' | 'low';
  readonly tone: BadgeTone;
}

const CRITICALITY_CLASS: Record<CriticalityLevel, string> = {
  [CriticalityLevel.HIGH]: 'bg-semaphore-red-bg text-semaphore-red-fg',
  [CriticalityLevel.MEDIUM]: 'bg-semaphore-amber-bg text-semaphore-amber-fg',
  [CriticalityLevel.LOW]: 'bg-semaphore-green-bg text-semaphore-green-fg',
};

const CRITICALITY_ICON: Record<CriticalityLevel, CriticalityPresentation['iconName']> = {
  [CriticalityLevel.HIGH]: 'high',
  [CriticalityLevel.MEDIUM]: 'medium',
  [CriticalityLevel.LOW]: 'low',
};

const CRITICALITY_TONE: Record<CriticalityLevel, BadgeTone> = {
  [CriticalityLevel.HIGH]: 'danger',
  [CriticalityLevel.MEDIUM]: 'warning',
  [CriticalityLevel.LOW]: 'success',
};

/**
 * Apresentação a partir da faixa JÁ RESOLVIDA pelo servidor.
 *
 * Este é o caminho normal da lista e do detalhe: o CORE devolve
 * `criticalityScore` e `criticalityLevel` calculados com os limiares da
 * organização. O cliente não recalcula.
 */
export function presentCriticality(params: {
  readonly score: number;
  readonly level: CriticalityLevel;
}): CriticalityPresentation {
  const label = CRITICALITY_LABEL_PT_BR[params.level];
  return {
    level: params.level,
    score: params.score,
    label,
    srText: `Criticidade GUT ${label.toLowerCase()}, pontuação ${params.score} de 125.`,
    className: CRITICALITY_CLASS[params.level],
    iconName: CRITICALITY_ICON[params.level],
    tone: CRITICALITY_TONE[params.level],
  };
}

/**
 * Pré-visualização no FORMULÁRIO, onde ainda não existe registro no servidor.
 *
 * Delega a `gutScore` e `criticalityFromScore` do domínio — as mesmas funções
 * que o CORE usa. Os limiares default são os de `DEFAULT_CRITICALITY_THRESHOLDS`
 * (1–20 baixa, 21–40 média, ≥41 alta), que são a faixa OFICIAL do legado
 * (§A.2.3); a outra faixa que existia lá (`≤5 / 6-10 / >10`) classificaria quase
 * tudo como crítico e é o BUG-NC-02.
 */
export function previewCriticality(
  score: GutScore,
  thresholds: CriticalityThresholds = DEFAULT_CRITICALITY_THRESHOLDS,
): CriticalityPresentation {
  const value = gutScore(score);
  return presentCriticality({ score: value, level: criticalityFromScore(value, thresholds) });
}

// ─────────────────────────────────────────────────────────────────────────────
// Semáforo de prazo da ação corretiva
// ─────────────────────────────────────────────────────────────────────────────

export interface CapaDuePresentation {
  readonly state: CapaDueState;
  readonly label: string;
  readonly srText: string;
  readonly className: string;
  readonly iconName: 'overdue' | 'due-soon' | 'on-time' | 'done';
  readonly tone: BadgeTone;
}

const DUE_LABEL: Record<CapaDueState, string> = {
  [CapaDueState.OVERDUE]: 'Vencida',
  [CapaDueState.DUE_SOON]: 'Vence em breve',
  [CapaDueState.ON_TIME]: 'No prazo',
  [CapaDueState.DONE]: 'Encerrada',
};

const DUE_CLASS: Record<CapaDueState, string> = {
  [CapaDueState.OVERDUE]: 'bg-semaphore-red-bg text-semaphore-red-fg',
  [CapaDueState.DUE_SOON]: 'bg-semaphore-amber-bg text-semaphore-amber-fg',
  [CapaDueState.ON_TIME]: 'bg-semaphore-green-bg text-semaphore-green-fg',
  [CapaDueState.DONE]: 'bg-semaphore-grey-bg text-semaphore-grey-fg',
};

const DUE_ICON: Record<CapaDueState, CapaDuePresentation['iconName']> = {
  [CapaDueState.OVERDUE]: 'overdue',
  [CapaDueState.DUE_SOON]: 'due-soon',
  [CapaDueState.ON_TIME]: 'on-time',
  [CapaDueState.DONE]: 'done',
};

const DUE_TONE: Record<CapaDueState, BadgeTone> = {
  [CapaDueState.OVERDUE]: 'danger',
  [CapaDueState.DUE_SOON]: 'warning',
  [CapaDueState.ON_TIME]: 'success',
  [CapaDueState.DONE]: 'neutral',
};

/**
 * Semáforo de prazo.
 *
 * `resolveCapaDueState` é a MESMA função que o CORE usa para preencher
 * `dueState` na listagem — e por construção os quatro baldes são mutuamente
 * exclusivos. No legado eles se sobrepunham: a mesma ação era contada em mais
 * de um balde do painel e a soma não fechava com o total (BUG-IND-01).
 *
 * Quando o servidor já mandou `dueState`, passe-o em `state`: recalcular no
 * cliente introduziria divergência por relógio dessincronizado. `dueDate` +
 * `status` existem para o caso em que a tela precisa reclassificar localmente,
 * como no formulário de criação da ação.
 */
export function presentCapaDue(params: {
  readonly state?: CapaDueState;
  readonly status: CapaActionStatus;
  readonly dueDate: Date;
  readonly now?: Date;
  readonly warningDays?: number;
}): CapaDuePresentation {
  const state =
    params.state ??
    resolveCapaDueState({
      status: params.status,
      dueDate: params.dueDate,
      now: params.now ?? new Date(),
      ...(params.warningDays === undefined ? {} : { warningDays: params.warningDays }),
    });

  return {
    state,
    label: DUE_LABEL[state],
    srText: buildDueSrText(state, params.dueDate),
    className: DUE_CLASS[state],
    iconName: DUE_ICON[state],
    tone: DUE_TONE[state],
  };
}

function buildDueSrText(state: CapaDueState, dueDate: Date): string {
  const formatted = formatIsoAsBr(toIsoDate(dueDate));
  switch (state) {
    case CapaDueState.OVERDUE:
      return `Prazo da ação: vencido em ${formatted}.`;
    case CapaDueState.DUE_SOON:
      return `Prazo da ação: vence em breve, em ${formatted}.`;
    case CapaDueState.ON_TIME:
      return `Prazo da ação: no prazo, vence em ${formatted}.`;
    case CapaDueState.DONE:
    default:
      return `Prazo da ação: encerrada. Prazo previsto era ${formatted}.`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tons das situações
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O tom é REFORÇO do rótulo textual, nunca o veículo da informação: em toda
 * tela o `<Badge>` exibe o texto e recebe `srPrefix` ("Situação:").
 */
export const NONCONFORMITY_STATUS_TONE: Record<NonconformityStatus, BadgeTone> = {
  [NonconformityStatus.OPEN]: 'info',
  [NonconformityStatus.UNDER_EVALUATION]: 'warning',
  [NonconformityStatus.NOT_APPLICABLE]: 'neutral',
  [NonconformityStatus.ACCEPTED]: 'brand',
  [NonconformityStatus.IN_TREATMENT]: 'warning',
  [NonconformityStatus.PENDING_EFFECTIVENESS]: 'warning',
  [NonconformityStatus.CLOSED]: 'success',
};

/**
 * Rótulos de situação da reunião.
 *
 * `MeetingStatus` vive em `@qualicloud/domain` (é máquina de estado, não valor
 * de contrato compartilhado com o Prisma) e não tem mapa de rótulo publicado —
 * ao contrário de `NONCONFORMITY_STATUS_LABEL_PT_BR` e companhia, que vêm de
 * `@qualicloud/contracts` e são importados direto. Este é o único mapa de
 * rótulo que a tela precisa declarar.
 */
export const MEETING_STATUS_LABEL: Record<MeetingStatus, string> = {
  [MeetingStatus.SCHEDULED]: 'Agendada',
  [MeetingStatus.IN_PROGRESS]: 'Em andamento',
  [MeetingStatus.AWAITING_CLOSURE]: 'Aguardando encerramento',
  [MeetingStatus.CLOSED]: 'Encerrada',
  [MeetingStatus.AWAITING_EFFECTIVENESS]: 'Aguardando análise de eficácia',
  [MeetingStatus.CANCELLED]: 'Cancelada',
};

export const MEETING_STATUS_TONE: Record<MeetingStatus, BadgeTone> = {
  [MeetingStatus.SCHEDULED]: 'info',
  [MeetingStatus.IN_PROGRESS]: 'brand',
  [MeetingStatus.AWAITING_CLOSURE]: 'warning',
  [MeetingStatus.CLOSED]: 'success',
  [MeetingStatus.AWAITING_EFFECTIVENESS]: 'warning',
  [MeetingStatus.CANCELLED]: 'neutral',
};

export const CAPA_ACTION_STATUS_TONE: Record<CapaActionStatus, BadgeTone> = {
  [CapaActionStatus.PLANNED]: 'neutral',
  [CapaActionStatus.IN_PROGRESS]: 'info',
  [CapaActionStatus.AWAITING_VERIFICATION]: 'warning',
  [CapaActionStatus.EFFECTIVE]: 'success',
  [CapaActionStatus.INEFFECTIVE]: 'danger',
  [CapaActionStatus.CANCELLED]: 'neutral',
};

export const ATTENDANCE_STATUS_TONE: Record<AttendanceStatus, BadgeTone> = {
  [AttendanceStatus.INVITED]: 'neutral',
  [AttendanceStatus.CONFIRMED]: 'info',
  [AttendanceStatus.DECLINED]: 'warning',
  [AttendanceStatus.ATTENDED]: 'success',
  [AttendanceStatus.ABSENT]: 'danger',
};

export const CIRCULAR_STATUS_TONE: Record<CircularStatus, BadgeTone> = {
  [CircularStatus.DRAFT]: 'outline',
  [CircularStatus.PUBLISHED]: 'success',
  [CircularStatus.ARCHIVED]: 'neutral',
};

// ─────────────────────────────────────────────────────────────────────────────
// Auxiliares de data
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `Date` → `AAAA-MM-DD` em componentes UTC.
 *
 * Usa UTC porque `dueDate` é DATE puro no banco: o servidor o serializa como
 * meia-noite UTC, e ler com `getFullYear()` local exibiria o dia anterior em
 * qualquer fuso a oeste de Greenwich. É o mesmo cuidado de `formatDateOnly`.
 */
export function toIsoDate(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatIsoAsBr(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
